import { IParticipantPerson } from 'Interfaces/participantPerson';
import { ContactModelV2 } from 'Models/ContactModelV2';
import { useConversationStore } from 'Modules/conversation/store/index.store';
import React from 'react';
import SimpleMarkdown, {
  HtmlOutputRule,
  ParserRule,
  ReactOutputRule,
} from 'simple-markdown';
import { Styled } from './buildMention.styles';

/**
 * A `SimpleMarkdown` `mention` rule ( returns React for display, and HTML for input)
 * @param participantPersons An array of wrapped `ParticipantModel`s, linked with their `PersonModel`s, used for Mentions.
 */
export const buildMention = (
  participantPersons: IParticipantPerson[]
): ParserRule & ReactOutputRule & HtmlOutputRule => ({
  order: SimpleMarkdown.defaultRules.paragraph.order + 0.5,
  match: (matchSrc) => RegExp(/^@pr([0-9]\d*)/).exec(matchSrc),
  parse: (capture, parse, state) => ({
    original: capture[0],
    content: parse(capture[1], state),
  }),
  react: (node, output, state) => {
    const resolvedPersonId = output(node.content, state).toString();
    const mentionedParticipant = participantPersons?.find(
      ({ person }) => person.sourceId === resolvedPersonId
    );

    let mentionedParticipantDetails: ContactModelV2 | undefined;

    // If mentioned participant doesn't have a DisplayName, then get it from the mentionUsers stored on useConversationStore
    if (!mentionedParticipant?.person?.name) {
      mentionedParticipantDetails = useConversationStore
        .getState()
        .mentionUsers.get(resolvedPersonId);
    }

    return (
      <Styled.Mention key={state.key} $unknown={!mentionedParticipant}>
        @
        {mentionedParticipant?.person?.name ||
          mentionedParticipantDetails?.name ||
          'Unknown User'}
        {mentionedParticipant?.person?.deleted && ' [Inactive]'}
      </Styled.Mention>
    );
  },
  html: (node, output, state) => {
    const resolvedPersonId = output(node.content, state).toString();
    const mentionedPtc = participantPersons.find(
      ({ person }) => person.sourceId === resolvedPersonId
    );
    // Note the extra surrounding spaces, this creates a gap that we can use to prevent the cursor from ending up inside the mention display text
    // This also happens in `Utils/turndownSvc`
    return mentionedPtc !== undefined
      ? `&nbsp;<div class="input-mention" mention-target="@pr${resolvedPersonId}" contenteditable="false">@${mentionedPtc.person.name}</div>&nbsp;`
      : `&nbsp;<div class="input-mention unknown" mention-target="@pr${resolvedPersonId}" contenteditable="false">@Unknown User</div>&nbsp;`;
  },
});
